import { Component, Input, OnInit } from '@angular/core';
import { OrderLineService, IOrderLine } from 'src/app/xena-api';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { chain, every, reduce, some } from 'underscore';
import { stringPropertyIgnoreCase } from 'src/app/utils';

@Component({
    selector: 'app-recalculate-sales-prices',
    templateUrl: './recalculate-sales-prices.component.html',
    styleUrls: ['./recalculate-sales-prices.component.scss'],
    standalone: false
})
export class RecalculateSalesPricesComponent implements OnInit {
  @Input() orderTaskId: number;
  open: boolean = true;
  saving: boolean = false;
  form: UntypedFormGroup;
  orderLines: { [index: number]: IOrderLine } = {};

  get articleGroups(): UntypedFormArray {
    return this.form.get('articleGroups') as UntypedFormArray;
  }

  constructor(private orderLineService: OrderLineService, private fb: UntypedFormBuilder) {
    this.form = this.fb.group({
      isSelected: [false],
      isIndeterminate: [false],
      calculationPercentage: [0],
      articleGroups: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.getOrderLines();
  }

  async getOrderLines() {
    const orderLines = await this.orderLineService.getByOrderTask(this.orderTaskId, { forceNoPaging: true }).toPromise();

    this.orderLines = reduce(orderLines.entities, (d, ol) => {
      d[ol.id] = ol;
      return d;
    }, {});
    this.form.setControl('articleGroups', this.fb.array(this.createArticleGroups(orderLines.entities)));
  }

  createArticleGroups(orderLines: IOrderLine[]): UntypedFormGroup[] {
    return chain<IOrderLine[]>(orderLines)
      .sortBy(ol => ol.articleGroupDescription?.toLowerCase())
      .groupBy(ol => ol.articleGroupDescription ?? $localize`:@@recalculatesSalesPrices_no_articleGroup:(None)`)
      .map((ols, key) => this.fb.group({
        isCollapsed: [true],
        isSelected: [false],
        isIndeterminate: [false],
        description: [key],
        priceNettTotalSum: reduce(ols, (sum, ol) => sum + ol.totals.priceNettTotal, 0),
        newPriceNettTotalSum: reduce(ols, (sum, ol) => sum + ol.totals.priceNettTotal, 0),
        orderLines: this.fb.array(ols.map(ol => this.fb.group({
          isSelected: [false],
          id: [ol.id],
          description: [ol.description],
          priceEach: [ol.priceEach],
          newPriceEach: [ol.priceEach],
          priceNettTotal: [ol.totals.priceNettTotal],
          newPriceNettTotal: [ol.totals.priceNettTotal]
        })))
      }))
      .value();
  }

  toggleSelectAll(): void {
    this.form.get('isIndeterminate').setValue(false);

    const isSelected = this.form.get('isSelected').value;
    this.articleGroups.controls.forEach(articleGroup => {
      articleGroup.patchValue({
        isSelected,
        isIndeterminate: false
      });

      const orderLines = articleGroup.get('orderLines') as UntypedFormArray;
      orderLines.controls.forEach(orderLine => {
        orderLine.get('isSelected').setValue(isSelected);
      });
    });
  }

  toggleSelectGroup(articleGroup: UntypedFormGroup): void {
    articleGroup.get('isIndeterminate').setValue(false);

    const isSelected = articleGroup.get('isSelected').value;
    const orderLines = articleGroup.get('orderLines') as UntypedFormArray;
    orderLines.controls.forEach(orderLine => {
      orderLine.get('isSelected').setValue(isSelected);
    });
    this.updateSelectAllStatus();
  }

  toggleSelect(articleGroup: UntypedFormGroup): void {
    this.updateSelectGroupStatus(articleGroup);
    this.updateSelectAllStatus();
  }

  updateSelectAllStatus(): void {
    const isSelected = chain<IArticleGroup[]>(this.articleGroups.value)
      .map(ag => ag.orderLines)
      .flatten()
      .map(ol => ol.isSelected)
      .value();

    const allSelected = every(isSelected);
    const someSelected = some(isSelected);

    this.form.patchValue({
      isSelected: allSelected,
      isIndeterminate: someSelected && !allSelected
    });
  }

  updateSelectGroupStatus(articleGroup: UntypedFormGroup): void {
    const isSelected = chain<IOrderLineConvert[]>(articleGroup.value.orderLines)
      .map(ol => ol.isSelected)
      .value();

    const allSelected = every(isSelected);
    const someSelected = some(isSelected);

    articleGroup.patchValue({
      isSelected: allSelected,
      isIndeterminate: someSelected && !allSelected
    });
  }

  toggleCollapse(articleGroup: UntypedFormGroup): void {
    const isCollapsed = articleGroup.get('isCollapsed');
    isCollapsed.setValue(!isCollapsed.value);
  }

  enableCalculateButton(): boolean {
    const percentage = this.form.get('calculationPercentage').value
    return percentage !== 0 && percentage >= -100 && chain<IArticleGroup[]>(this.articleGroups.value)
      .map(ag => ag.orderLines)
      .flatten()
      .any(ol => ol.isSelected)
      .value();
  }

  calculate() {
    const calculationPercentage: number = this.form.get('calculationPercentage').value;
    if (calculationPercentage >= -100) {
      const factor = (calculationPercentage + 100) / 100;

      this.articleGroups.controls.forEach((articleGroup: UntypedFormGroup) => {
        const orderLines = articleGroup.get('orderLines') as UntypedFormArray;
        let newPriceNettTotalSum = 0;
        orderLines.controls.forEach((orderLine: UntypedFormGroup) => {
          if (orderLine.get('isSelected').value) {
            orderLine.patchValue({
              isSelected: false,
              newPriceEach: orderLine.get('newPriceEach').value * factor,
              newPriceNettTotal: orderLine.get('newPriceNettTotal').value * factor
            });
          }
          newPriceNettTotalSum += orderLine.get('newPriceNettTotal').value;
        });
        articleGroup.patchValue({
          isSelected: false,
          isIndeterminate: false,
          newPriceNettTotalSum
        });
      });
    }

    this.form.patchValue({
      isSelected: false,
      isIndeterminate: false,
      calculationPercentage: 0
    });
  }

  resetAll() {
    this.articleGroups.controls.forEach((articleGroup: UntypedFormGroup) => {
      const orderLines = articleGroup.get('orderLines') as UntypedFormArray
      orderLines.controls.forEach((orderLine: UntypedFormGroup) => {
        orderLine.patchValue({
          isSelected: false,
          newPriceEach: orderLine.get('priceEach').value,
          newPriceNettTotal: orderLine.get('priceNettTotal').value
        });
      })
      articleGroup.patchValue({
        isSelected: false,
        isIndeterminate: false,
        newPriceNettTotalSum: articleGroup.get('priceNettTotalSum').value
      });
    });

    this.form.patchValue({
      isSelected: false,
      isIndeterminate: false,
      calculationPercentage: 0
    });
  }

  cancel() {
    this.open = false;
  }

  async save() {
    this.saving = true;

    const orderLines = chain<IArticleGroup[]>(this.articleGroups.value)
      .map(ag => ag.orderLines)
      .flatten()
      .filter(ol => ol.priceNettTotal !== ol.newPriceNettTotal)
      .map(({ id, newPriceNettTotal, newPriceEach }) => {
        const ol = this.orderLines[id];
        ol.priceEach = newPriceEach;
        ol.totals.priceNettTotal = newPriceNettTotal;
        return ol;
      })
      .value();

    await this.orderLineService.putBulk(orderLines).toPromise();
    this.open = false;
  }
}

export interface IArticleGroup {
  isCollapsed: boolean,
  isSelected: boolean,
  isIndeterminate: boolean,
  description: string,
  priceNettTotalSum: number,
  newPriceNettTotalSum: number,
  orderLines: IOrderLineConvert[]
}

export interface IOrderLineConvert {
  isSelected: boolean,
  isIndeterminate: boolean,
  id: number
  description: string,
  priceEach: number,
  newPriceEach: number,
  priceNettTotal: number,
  newPriceNettTotal: number
}
